import { React, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import Dashboard from './components/Dashboard';
import Alerts from './components/Alerts';
import Wards from './components/Wards';
import Preferences from './components/Preferences';
import Subscription from './components/Subscription';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './components/Login'
import Register from './components/Register'
import RoleBasedRoute from './components/RoleBasedRoute';
import './styles/style.css';
import logoImage from './assets/logo.png';
import { supabase } from './services/supabaseClient';
import { ThemeProvider, useTheme } from './context/ThemeContext';

// Sidebar component with wider layout
const Sidebar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const currentPath = location.pathname;
  const { theme, toggleTheme } = useTheme();

  const storedUser = JSON.parse(localStorage.getItem('user'));
  const user = {
    name: storedUser?.full_name || "Jane Doe",
    role: storedUser?.role || "Unknown",
    initials: storedUser?.full_name?.split(" ").map(word => word[0]).join("").toUpperCase() || 'NA'
  };

  const handleLogout = async () => {
    console.log('Logging out...');
    localStorage.removeItem('user');
    await supabase.auth.signOut();
    window.location.href = "/login";
  };

  const close = () => setMobileOpen(false);

  return (
    <>
      {/* Hamburger toggle — mobile only */}
      <button
        className={`hamburger-btn${mobileOpen ? ' open' : ''}`}
        onClick={() => setMobileOpen(v => !v)}
        aria-label="Toggle navigation"
      >
        <span /><span /><span />
      </button>

      {/* Backdrop */}
      {mobileOpen && <div className="sidebar-overlay" onClick={close} />}

      <div className={`sidebar${mobileOpen ? ' open' : ''}`}>
        {/* Logo */}
        <Link to="/" className="sidebar-logo" onClick={close}>
          <img src={logoImage} alt="Guardian Sphere Logo" className="logo-image" />
          <div className="logo-text">
            <div>GUARDIAN SPHERE </div>
          </div>
        </Link>

        {/* Main Navigation */}
        {user.role !== 'ward' && (
          <div className="sidebar-section">
            <div className="sidebar-section-title">MAIN</div>
            <Link to="/" className={`nav-item ${currentPath === '/' ? 'active' : ''}`} onClick={close}>
              <i className="fa-solid fa-shield"></i>
              <span>Dashboard</span>
            </Link>
            <Link to="/alerts" className={`nav-item ${currentPath === '/alerts' ? 'active' : ''}`} onClick={close}>
              <i className="fa-solid fa-bell"></i>
              <span>Alerts</span>
            </Link>
            <Link to="/wards" className={`nav-item ${currentPath === '/wards' ? 'active' : ''}`} onClick={close}>
              <i className="fa-solid fa-users"></i>
              <span>Wards</span>
            </Link>
          </div>
        )}

        {/* Settings Navigation */}
        <div className="sidebar-section">
          <div className="sidebar-section-title">SETTINGS</div>
          <Link to="/preferences" className={`nav-item ${currentPath === '/preferences' ? 'active' : ''}`} onClick={close}>
            <i className="fa-solid fa-gear"></i>
            <span>Preferences</span>
          </Link>
          {user.role !== 'ward' && (
            <Link to="/subscription" className={`nav-item ${currentPath === '/subscription' ? 'active' : ''}`} onClick={close}>
              <i className="fa-solid fa-credit-card"></i>
              <span>Subscription</span>
            </Link>
          )}
        </div>

        {/* User Section */}
        <div className="user-section">
          <div className="theme-toggle">
            <button
              className={`theme-option ${theme === 'dark' ? 'active' : ''}`}
              onClick={() => theme !== 'dark' && toggleTheme()}
            >
              <i className="fa-solid fa-moon" style={{ fontSize: '0.5625rem' }} />
              {' '}DARK
            </button>
            <button
              className={`theme-option ${theme === 'light' ? 'active' : ''}`}
              onClick={() => theme !== 'light' && toggleTheme()}
            >
              <i className="fa-solid fa-sun" style={{ fontSize: '0.5625rem' }} />
              {' '}LIGHT
            </button>
          </div>
          <div className="user-info">
            <div className="user-avatar">{user.initials}</div>
            <div className="user-details">
              <div className="user-name">{user.name}</div>
              <div className="user-role">{user.role}</div>
            </div>
          </div>
          <button className="logout-btn" onClick={handleLogout}>
            <i className="fa-solid fa-right-from-bracket"></i>
            Log out
          </button>
        </div>
      </div>
    </>
  );
};

const Layout = ({ children }) => {
  return (
    <div className="app-container">
      <Sidebar />
      <div className="main-content">
        {children}
      </div>
    </div>
  );
};

function App() {
  return (
    <ThemeProvider>
    <Router>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />

        {/* Protected Routes */}
        <Route
          path="*"
          element={
            <ProtectedRoute>
              <Layout>
                <Routes>
                  <Route path="/" element={
                    <RoleBasedRoute restrictedRoles={['ward']}>
                      <Dashboard />
                    </RoleBasedRoute>
                  } />
                  <Route path="/alerts" element={
                    <RoleBasedRoute restrictedRoles={['ward']}>
                      <Alerts />
                    </RoleBasedRoute>
                  } />
                  <Route path="/wards" element={
                    <RoleBasedRoute restrictedRoles={['ward']}>
                      <Wards />
                    </RoleBasedRoute>
                  } />
                  <Route path="/preferences" element={<Preferences />} />
                  <Route path="/subscription" element={
                    <RoleBasedRoute restrictedRoles={['ward']}>
                      <Subscription />
                    </RoleBasedRoute>
                  } />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Layout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </Router>
    </ThemeProvider>
  );
}

export default App;